import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, visitsAPI, clientsAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import VisitEditDialog from '../components/VisitEditDialog';
import CaregiverOpenShifts from '../components/CaregiverOpenShifts';
import CaregiverSmsSettings from '../components/CaregiverSmsSettings';
import OnboardingAlerts from '../components/onboarding/OnboardingAlerts';
import BillingCycleAlert from '../components/billing/BillingCycleAlert';
import {
  Users,
  Calendar,
  CheckCircle,
  Clock,
  UserCircle,
  ArrowRight,
  TrendingUp,
  UserPlus
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayVisits, setTodayVisits] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [caregiversMap, setCaregiversMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Add Supervisor dialog state
  const [isAddSupervisorOpen, setIsAddSupervisorOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', phone: '', role: 'supervisor' });
  const [creating, setCreating] = useState(false);
  const canManageUsers = user?.role === 'supervisor' || user?.role === 'super_admin';

  // Edit visit dialog (from clicking a today's visit row)
  const [editVisitOpen, setEditVisitOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const canEditVisits = user?.role === 'supervisor' || user?.role === 'super_admin';

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      await usersAPI.create({
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password,
        phone: newUser.phone.trim() || null,
        role: newUser.role,
        office_id: user?.office_id || null
      });
      toast.success(`${newUser.role === 'supervisor' ? 'Supervisor' : 'User'} created — ${newUser.email}`);
      setIsAddSupervisorOpen(false);
      setNewUser({ name: '', email: '', password: '', phone: '', role: 'supervisor' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [statsRes, visitsRes, clientsRes, caregiversRes] = await Promise.all([
        dashboardAPI.getStats(),
        visitsAPI.getAll({}),
        clientsAPI.getAll().catch(() => ({ data: [] })),
        usersAPI.getAll('caregiver').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);

      const cMap = {};
      (clientsRes.data || []).forEach(c => { cMap[c.id] = c.name; });
      setClientsMap(cMap);

      const cgMap = {};
      (caregiversRes.data || []).forEach(cg => { cgMap[cg.id] = cg.name; });
      setCaregiversMap(cgMap);

      const todays = (visitsRes.data || [])
        .filter(v => v.scheduled_date === today)
        .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''));
      setTodayVisits(todays);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your {user?.role === 'supervisor' ? 'agency' : 'care'} today.
          </p>
        </div>
        <div className="flex gap-3">
          {canManageUsers && (
            <Button variant="outline" className="rounded-xl gap-2" onClick={() => setIsAddSupervisorOpen(true)} data-testid="add-supervisor-btn">
              <UserPlus weight="duotone" className="w-4 h-4" />
              Add Supervisor
            </Button>
          )}
          {user?.role === 'supervisor' && (
            <>
              <Link to="/schedule">
                <Button className="rounded-xl gap-2" data-testid="schedule-visit-btn">
                  <Calendar weight="duotone" className="w-4 h-4" />
                  Schedule Visit
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {['supervisor', 'admin', 'director', 'super_admin'].includes(user?.role) && <><BillingCycleAlert /><OnboardingAlerts /></>}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {user?.role === 'supervisor' && (
          <>
            <Card className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid="stat-total-clients">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Active Clients</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.total_clients || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid="stat-caregivers">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Active Caregivers</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.total_caregivers || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                    <UserCircle weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid="stat-today-visits">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Today's Visits</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.today_visits || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                    <Calendar weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid="stat-completed">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Completed</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.completed_visits || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle weight="duotone" className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {user?.role === 'caregiver' && (
          <>
            <Card className="rounded-2xl border border-border" data-testid="stat-my-assignments">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">My Clients</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.my_assignments || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border" data-testid="stat-my-visits">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Today's Visits</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.my_visits_today || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                    <Calendar weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border" data-testid="stat-my-completed">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Completed Today</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.my_completed || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle weight="duotone" className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {user?.role === 'client' && (
          <>
            <Card className="rounded-2xl border border-border" data-testid="stat-upcoming-visits">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Upcoming Visits</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.upcoming_visits || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Calendar weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border" data-testid="stat-my-caregivers">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">My Caregivers</p>
                    <h3 className="font-heading text-3xl font-semibold mt-2">{stats?.my_caregivers || 0}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                    <UserCircle weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Caregiver: Available Shifts Near You (only shows when >0) */}
      {user?.role === 'caregiver' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2"><CaregiverOpenShifts /></div>
          <div><CaregiverSmsSettings /></div>
        </div>
      )}

      {/* Today's Visits */}
      <Card className="rounded-2xl border border-border" data-testid="recent-visits-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg">Today's Visits</CardTitle>
            <CardDescription>{format(new Date(), 'EEEE, MMMM d, yyyy')} — {todayVisits.length} scheduled</CardDescription>
          </div>
          <Link to="/schedule">
            <Button variant="ghost" size="sm" className="rounded-xl gap-1">
              View All
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {todayVisits.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No visits scheduled today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayVisits.map((visit) => {
                const clientName = clientsMap[visit.client_id] || 'Unknown client';
                const isUnassigned = !visit.caregiver_id || visit.caregiver_id === '' || visit.caregiver_id === 'none';
                const caregiverName = isUnassigned ? 'Unassigned' : (caregiversMap[visit.caregiver_id] || 'Unknown caregiver');
                return (
                  <div
                    key={visit.id}
                    className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                      isUnassigned
                        ? 'bg-red-50 border-2 border-red-400 hover:bg-red-100'
                        : 'bg-muted/30 hover:bg-muted/50'
                    } ${canEditVisits ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (canEditVisits) { setEditingVisit(visit); setEditVisitOpen(true); }
                    }}
                    data-testid={`visit-item-${visit.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isUnassigned ? 'bg-red-100' : 'bg-primary/10'
                      }`}>
                        <UserCircle weight="duotone" className={`w-5 h-5 ${isUnassigned ? 'text-red-600' : 'text-primary'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-foreground" data-testid={`visit-client-${visit.id}`}>
                          {clientName}
                        </p>
                        <p className={`text-sm ${isUnassigned ? 'text-red-700 font-medium' : 'text-muted-foreground'}`} data-testid={`visit-caregiver-${visit.id}`}>
                          {isUnassigned ? 'UNASSIGNED CAREGIVER' : `Caregiver: ${caregiverName}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isUnassigned && (
                        <Badge className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid={`visit-unassigned-badge-${visit.id}`}>
                          Needs Assignment
                        </Badge>
                      )}
                      <Badge className={`${getStatusColor(visit.status)} rounded-full`}>
                        {visit.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Supervisor / User Dialog */}
      <Dialog open={isAddSupervisorOpen} onOpenChange={setIsAddSupervisorOpen}>        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create User Login</DialogTitle>
            <DialogDescription>Add a new supervisor, caregiver, or client account. They can log in immediately with the email and password you set.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="rounded-xl" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="caregiver">Caregiver</SelectItem>
                  <SelectItem value="client">Client / Family</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Jane Doe" className="rounded-xl" required data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="jane@homecare.com" className="rounded-xl" required data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password * <span className="text-xs text-muted-foreground">(minimum 6 characters)</span></Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Set a temporary password" className="rounded-xl" required minLength={6} data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} placeholder="(555) 555-5555" className="rounded-xl" data-testid="new-user-phone" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsAddSupervisorOpen(false)} disabled={creating}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={creating} data-testid="create-user-submit">
                {creating ? 'Creating...' : 'Create Login'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Visit Dialog (opened by clicking a today's visit) */}
      <VisitEditDialog
        open={editVisitOpen}
        onOpenChange={setEditVisitOpen}
        visit={editingVisit}
        onSaved={loadDashboardData}
      />
    </div>
  );
};

export default DashboardPage;
